import { useEffect, useRef } from 'react';

import { useRovoAgentActionAnalytics } from '@atlaskit/rovo-agent-analytics/actions';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';

import { getCreateAgentAnalyticsAttributes } from './analytics/getCreateAgentAnalyticsAttributes';
import { getPlanAnalyticsAttributes } from './analytics/getPlanAnalyticsAttributes';
import { GASV3_ANALYTICS_TOUCHPOINT_NAME } from './constants';

type AgentPlanAnalyticsProps = {
	actionRenderer: ActionRendererFor<typeof ActionKeys.StudioCreateAgent>;
};

/**
 * Tracks agent plan generated and viewed events
 */
export const AgentPlanAnalytics = ({ actionRenderer }: AgentPlanAnalyticsProps) => {
	const { conversationId, messageId, isHistoricalMessage } = actionRenderer;
	const { invocationId } = actionRenderer.actions.at(0) ?? {};
	const { flowKey, issueId } = actionRenderer.actions.at(0)?.data.agentMetadata?.jiraMetadata ?? {};
	const actionRef = useRef(actionRenderer.actions.at(0));

	const { trackAgentEvent } = useRovoAgentActionAnalytics({
		touchPoint: GASV3_ANALYTICS_TOUCHPOINT_NAME,
	});

	useEffect(() => {
		const plan = actionRef.current?.data.agent;

		if (!plan) return;

		const attributes = {
			conversationId,
			invocationId,
			messageId,
			...getCreateAgentAnalyticsAttributes({ flowKey, issueId }),
			...getPlanAnalyticsAttributes(plan),
		};

		trackAgentEvent({
			action: 'createFlowPlanViewed',
			actionSubject: 'rovoAgent',
			attributes,
		});

		if (!isHistoricalMessage) {
			trackAgentEvent({
				action: 'createFlowPlanGenerated',
				actionSubject: 'rovoAgent',
				attributes,
			});
		}
	}, [
		trackAgentEvent,
		conversationId,
		invocationId,
		messageId,
		isHistoricalMessage,
		flowKey,
		issueId,
	]);

	return null;
};
